import dayjs from 'dayjs';
import { P } from '../theme/tokens';
import { TYPE_META, bucketFor, cap, formatPhone, propertyFor } from '../utils/callLog';

const BUCKETS = {
  emergency: { label: 'Emergency', color: P.danger },
  leasing: { label: 'Leasing', color: P.info },
  owner: { label: 'Owner', color: P.purple },
  hvac: { label: 'HVAC', color: P.cyan },
  maintenance: { label: 'Maintenance', color: P.warning },
  other: { label: 'Call', color: P.subtle }
};

const ROLE_BY_TYPE = {
  lead: 'Prospect',
  tour: 'Prospect',
  owner_request: 'Owner',
  maintenance_request: 'Tenant'
};

export const STATUS = {
  new: { label: 'Not started', color: P.info },
  in_progress: { label: 'In progress', color: P.warning },
  resolved: { label: 'Completed', color: P.success },
  archived: { label: 'Archived', color: P.subtle }
};

const dateLabel = (d) => {
  const now = dayjs();
  if (d.isSame(now, 'day')) return 'Today';
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format(d.isSame(now, 'year') ? 'MMM D' : 'MMM D, YYYY');
};

const duration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function parseTranscript(text) {
  if (!text) return [];
  return String(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce((turns, line) => {
      const match = line.match(/^(AI|Assistant|Bot|User|Customer|Caller)\s*:\s*(.*)$/i);
      if (match) turns.push({ ai: /^(ai|assistant|bot)$/i.test(match[1]), text: match[2] });
      else if (turns.length) turns[turns.length - 1].text += ` ${line}`;
      else turns.push({ ai: true, text: line });
      return turns;
    }, []);
}

const typeOf = (item) => {
  const bucket = bucketFor(item);
  const meta = BUCKETS[bucket] || BUCKETS.other;
  if (bucket === 'emergency') {
    const label = item.data?.category || item.data?.emergencyType;
    return { bucket, color: meta.color, label: label ? cap(String(label)) : meta.label };
  }
  if (bucket === 'other') return { bucket, color: meta.color, label: TYPE_META[item?.type]?.label || meta.label };
  return { bucket, ...meta };
};

const statusOf = (record, item) => {
  if (record.status === 'in_progress') return { label: 'Live', color: P.info };
  if (record.attention?.needed) return { label: 'Needs attention', color: P.purple };
  return STATUS[item?.status] || null;
};

export function adaptCall(record) {
  if (!record) return null;
  const item = record.captured?.[0] || null;
  const at = dayjs(record.startedAt || record.createdAt);
  const phone = record.direction === 'outbound' ? record.toNumber : record.fromNumber;
  const language = item?.data?.language || record.classification?.language || '';

  return {
    id: record._id,
    name: item?.contact?.name || record.contact?.name || formatPhone(phone),
    phone: formatPhone(phone),
    role: ROLE_BY_TYPE[item?.type] || '',
    language: language && language.toLowerCase() !== 'en' ? language.toUpperCase() : '',
    date: at.isValid() ? dateLabel(at) : '',
    time: at.isValid() ? at.format('h:mm A') : '',
    startedAt: record.startedAt || record.createdAt || null,
    type: typeOf(item),
    status: statusOf(record, item),
    property: propertyFor(record, item).replace(/^—$/, ''),
    summary: record.summary || item?.title || '',
    action: item?.data?.actionItem || item?.title || '',
    duration: duration(record.durationSec),
    transcript: parseTranscript(record.transcript),
    hasRecording: Boolean(record.recordingUrl || record.providerCallId),
    reviewed: Boolean(record.reviewed),
    needsAttention: Boolean(record.attention?.needed),
    direction: record.direction || 'inbound',
    handledBy: record.handledBy || 'ai',
    from: formatPhone(record.fromNumber),
    to: formatPhone(record.toNumber),
    rawStatus: record.status || '',
    outcome: record.outcome || '',
    endedAt: record.endedAt || null,
    endedReason: record.endedReason || '',
    urgency: record.classification?.urgency || '',
    callerRole: record.classification?.callerRole || '',
    intent: record.classification?.intent || '',
    why: record.classification?.why || record.classification?.nextStep || '',
    providerCallId: record.providerCallId || ''
  };
}
